import api from "@/services/api";

/**
 * @description Servicio para interactuar con el endpoint /practice-catalog.
 * Gestiona el CRUD del maestro de prestaciones (prácticas, tratamientos,
 * medicamentos, insumos, prótesis).
 */
const practiceCatalogService = {};

const endpoint = "/practice-catalog";

/**
 * @description Obtiene listado paginado de prestaciones con filtros
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} [params.type] - Filtrar por tipo (practice, treatment, medication, etc.)
 * @param {string} [params.category] - Filtrar por categoría
 * @param {string} [params.searchInput] - Texto de búsqueda
 * @param {boolean} [params.isActive] - Filtrar por estado
 * @param {number} [params.offset] - Offset para paginación
 * @param {number} [params.limit] - Límite de resultados
 * @returns {Promise<{docs: Array, total: number}>} Resultado paginado
 */
practiceCatalogService.query = function (params) {
  return api.get(endpoint, { params }).then((res) => res.data);
};

/**
 * @description Obtiene una prestación por su ID
 * @param {string} id - ID de la prestación
 * @returns {Promise<Object>} Datos de la prestación
 */
practiceCatalogService.getById = function (id) {
  return api.get(`${endpoint}/${id}`).then((res) => res.data);
};

/**
 * @description Crea una nueva prestación en el maestro
 * @param {Object} data - Datos de la prestación
 * @param {string} data.code - Código de nomenclador
 * @param {string} data.name - Nombre
 * @param {string} data.type - Tipo
 * @param {string} data.category - Categoría
 * @returns {Promise<Object>} Prestación creada
 */
practiceCatalogService.create = function (data) {
  return api.post(endpoint, data).then((res) => res.data);
};

/**
 * @description Actualiza una prestación existente
 * @param {string} id - ID de la prestación
 * @param {Object} data - Campos a actualizar
 * @returns {Promise<Object>} Prestación actualizada
 */
practiceCatalogService.update = function (id, data) {
  return api.put(`${endpoint}/${id}`, data).then((res) => res.data);
};

/**
 * @description Desactiva una prestación (soft delete)
 * @param {string} id - ID de la prestación
 * @returns {Promise<void>}
 */
practiceCatalogService.remove = function (id) {
  return api.delete(`${endpoint}/${id}`);
};

/**
 * @description Importa prestaciones desde archivo Excel/CSV
 * @param {File} file - Archivo a importar (.xlsx, .xls, .csv)
 * @param {string} type - Tipo de prestación (practice, treatment, medication, etc.)
 * @param {string} category - Categoría de la prestación
 * @returns {Promise<{imported: number, duplicates: number, total: number, errors: Array}>} Resultado de la importación
 */
practiceCatalogService.import = function (file, type, category) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  formData.append("category", category);
  return api
    .post(`${endpoint}/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
};

export default practiceCatalogService;
